import React, { useState, useEffect } from 'react';
import { useAuth, useLanguage, API_URL } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  HelpCircle, Bot, Phone, Mail, MapPin, ChevronRight, Search,
  MessageSquare, FileText, ExternalLink, Shield
} from 'lucide-react';

const WHATSAPP_NUMBER = '14313345085';

// Province list for the intelligent agent
const RDC_PROVINCES = [
  "Kinshasa", "Kwilu", "Kwango", "Mai-Ndombe", "Kongo Central",
  "Equateur", "Mongala", "Nord-Ubangi", "Sud-Ubangi", "Tshuapa",
  "Tshopo", "Bas-Uele", "Haut-Uele", "Ituri",
  "Nord-Kivu", "Sud-Kivu", "Maniema",
  "Tanganyika", "Haut-Lomami", "Lualaba", "Haut-Katanga",
  "Kasai", "Kasai-Central", "Kasai-Oriental", "Lomami", "Sankuru",
];

// Intelligent Support Chat
const SupportChat = ({ agents, fr }) => {
  const [step, setStep] = useState('welcome');
  const [province, setProvince] = useState('');
  const [filteredProvinces, setFilteredProvinces] = useState([]);
  const [foundAgents, setFoundAgents] = useState([]);
  const [searchText, setSearchText] = useState('');

  const searchProvince = (text) => {
    setSearchText(text);
    if (text.length >= 2) {
      setFilteredProvinces(RDC_PROVINCES.filter(p => p.toLowerCase().includes(text.toLowerCase())));
    } else {
      setFilteredProvinces([]);
    }
  };

  const selectProvince = (prov) => {
    setProvince(prov);
    setStep('result');
    const matched = agents.filter(a =>
      a.province.toLowerCase() === prov.toLowerCase() ||
      (a.city && a.city.toLowerCase() === prov.toLowerCase())
    );
    setFoundAgents(matched);
  };

  const reset = () => {
    setStep('welcome');
    setProvince('');
    setSearchText('');
    setFilteredProvinces([]);
    setFoundAgents([]);
  };

  return (
    <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-slate-700 overflow-hidden" data-testid="support-chat">
      <div className="bg-gradient-to-r from-[#0F2C59] to-[#007BFF] p-5 text-white">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white/15 rounded-xl flex items-center justify-center">
            <Bot className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-heading font-bold">Agent de Support JoSmart</h3>
            <p className="text-white/70 text-xs">Trouvez votre agent de support local</p>
          </div>
        </div>
      </div>

      <div className="p-5 min-h-[300px]">
        {step === 'welcome' && (
          <div className="space-y-4">
            <div className="bg-slate-50 dark:bg-slate-700 rounded-2xl rounded-bl-md p-4">
              <p className="text-sm text-slate-700 dark:text-slate-200">
                Bonjour ! Je suis l'agent de support JoSmart. Pour vous mettre en contact avec 
                le representant le plus proche, veuillez indiquer votre <strong>province ou ville</strong>.
              </p>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-3.5 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Tapez votre province ou ville..."
                value={searchText}
                onChange={(e) => searchProvince(e.target.value)}
                className="pl-10 h-11"
                data-testid="support-province-search"
              />
            </div>
            {filteredProvinces.length > 0 && (
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {filteredProvinces.map(p => (
                  <button
                    key={p}
                    onClick={() => selectProvince(p)}
                    className="w-full text-left px-4 py-2.5 rounded-lg hover:bg-blue-50 dark:hover:bg-slate-700 text-sm text-slate-700 dark:text-slate-200 flex items-center gap-2 transition-colors"
                    data-testid={`province-${p.toLowerCase()}`}
                  >
                    <MapPin className="w-3.5 h-3.5 text-slate-400" /> {p}
                  </button>
                ))}
              </div>
            )}
            {searchText.length >= 2 && filteredProvinces.length === 0 && (
              <p className="text-sm text-slate-400 text-center py-3">
                Aucune province trouvee. Essayez "Kinshasa" ou "Kwilu".
              </p>
            )}
            {searchText.length < 2 && (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {["Kinshasa", "Kwilu", "Nord-Kivu", "Sud-Kivu", "Haut-Katanga", "Equateur", "Kongo Central", "Kasai"].map(p => (
                  <button key={p} onClick={() => selectProvince(p)} className="px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-700 hover:bg-blue-50 dark:hover:bg-slate-600 text-xs text-slate-600 dark:text-slate-300 font-medium transition-colors text-center">
                    {p}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {step === 'result' && (
          <div className="space-y-4">
            <div className="bg-slate-50 dark:bg-slate-700 rounded-2xl rounded-bl-md p-4">
              <p className="text-sm text-slate-700 dark:text-slate-200">
                Voici les contacts de support pour <strong>{province}</strong> :
              </p>
            </div>

            {foundAgents.length > 0 ? (
              foundAgents.map((agent, i) => (
                <div key={agent.id || i} className="bg-emerald-50 dark:bg-emerald-900/30 rounded-xl p-4 border border-emerald-200 dark:border-emerald-800" data-testid={`agent-card-${i}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <Shield className="w-4 h-4 text-emerald-600" />
                    <span className="font-semibold text-slate-800 dark:text-slate-200 text-sm">{agent.agent_name}</span>
                    <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Responsable {agent.province}</Badge>
                  </div>
                  <div className="space-y-2">
                    <a href={`tel:${agent.phone}`} className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300 hover:text-emerald-600">
                      <Phone className="w-3.5 h-3.5" /> {agent.phone}
                    </a>
                    <a href={`mailto:${agent.email}`} className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300 hover:text-emerald-600">
                      <Mail className="w-3.5 h-3.5" /> {agent.email}
                    </a>
                    <a href={`https://wa.me/${agent.phone.replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-emerald-600 hover:text-emerald-700 font-medium">
                      <MessageSquare className="w-3.5 h-3.5" /> Contacter via WhatsApp
                    </a>
                  </div>
                </div>
              ))
            ) : (
              <div className="bg-amber-50 dark:bg-amber-900/30 rounded-xl p-4 border border-amber-200 dark:border-amber-800">
                <p className="text-sm text-amber-800 dark:text-amber-200">
                  Aucun agent n'est encore disponible dans la province de <strong>{province}</strong>.
                  Contactez notre support central :
                </p>
                <div className="mt-3 space-y-2">
                  <a href="mailto:support@josmarttech.net" className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300 hover:text-blue-600">
                    <Mail className="w-3.5 h-3.5" /> support@josmarttech.net
                  </a>
                </div>
              </div>
            )}

            <Button variant="outline" size="sm" onClick={reset} className="w-full" data-testid="support-reset">
              Chercher une autre province
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};


export const SupportPage = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const [agents, setAgents] = useState([]);
  const fr = language === 'fr';
  const role = user?.role;
  const isDirection = ['super_admin', 'co_admin', 'rector', 'vice_rector_academic', 'sg_admin', 'sg_academic', 'dean', 'vice_dean'].includes(role);

  useEffect(() => {
    loadAgents();
  }, []);

  const loadAgents = async () => {
    try {
      const res = await axios.get(`${API_URL}/support/agents`);
      setAgents(res.data.agents || []);
    } catch (err) { console.error(err); }
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="support-page">
      <div>
        <h1 className="text-2xl font-heading font-bold text-slate-900 dark:text-white">
          {fr ? 'Aide & Support' : 'Help & Support'}
        </h1>
        <p className="text-slate-400 text-sm mt-1">
          {fr ? 'Trouvez de l\'aide et contactez notre equipe' : 'Find help and contact our team'}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Intelligent Support Agent */}
        <SupportChat agents={agents} fr={fr} />

        {/* Quick Help Links */}
        <div className="space-y-4">
          {/* JosAide */}
          <Link to="/ai-assistant" className="block bg-white dark:bg-slate-800 rounded-2xl p-5 border border-slate-100 dark:border-slate-700 hover:border-[#007BFF]/30 hover:shadow-md transition-all group" data-testid="support-inu-link">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-[#0F2C59] to-[#007BFF] rounded-xl flex items-center justify-center">
                <Bot className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-heading font-bold text-slate-800 dark:text-white text-sm">JosAide</h3>
                <p className="text-slate-400 text-xs">Assistant IA pour naviguer dans l'application</p>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-[#007BFF]" />
            </div>
          </Link>

          {/* Email Support */}
          <a href="mailto:support@josmarttech.net" className="block bg-white dark:bg-slate-800 rounded-2xl p-5 border border-slate-100 dark:border-slate-700 hover:shadow-md transition-all" data-testid="support-email">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-50 dark:bg-blue-900/30 rounded-xl flex items-center justify-center">
                <Mail className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-heading font-bold text-slate-800 dark:text-white text-sm">Email Support</h3>
                <p className="text-blue-600 text-xs">support@josmarttech.net</p>
              </div>
            </div>
          </a>

          {/* WhatsApp - Only for direction roles */}
          {isDirection && (
            <a href={`https://wa.me/${WHATSAPP_NUMBER}`} target="_blank" rel="noopener noreferrer" className="block bg-white dark:bg-slate-800 rounded-2xl p-5 border border-slate-100 dark:border-slate-700 hover:shadow-md transition-all" data-testid="support-whatsapp-direction">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-50 dark:bg-green-900/30 rounded-xl flex items-center justify-center">
                  <Phone className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-slate-800 dark:text-white text-sm">WhatsApp Direction</h3>
                  <p className="text-green-600 text-xs">+1 (431) 334-5085</p>
                  <p className="text-slate-400 text-[10px]">Reserve a la direction</p>
                </div>
              </div>
            </a>
          )}

          {/* Legal Links */}
          <Link to="/legal" className="block bg-white dark:bg-slate-800 rounded-2xl p-5 border border-slate-100 dark:border-slate-700 hover:shadow-md transition-all" data-testid="support-legal">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-slate-100 dark:bg-slate-700 rounded-xl flex items-center justify-center">
                <FileText className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <h3 className="font-heading font-bold text-slate-800 dark:text-white text-sm">Mentions Legales</h3>
                <p className="text-slate-400 text-xs">Confidentialite, Conditions d'utilisation</p>
              </div>
            </div>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default SupportPage;
